"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoginMain = void 0;
const react_1 = __importStar(require("react"));
const core_1 = require("@veeam/core");
const public_1 = require("../../../common/public");
const ErrorMessage_1 = require("../../../ErrorMessage");
const fields_1 = require("../../../fields");
const helpers_1 = require("../../../helpers");
const layouts_1 = require("../../../layouts");
const FormLayout_1 = require("../../../layouts/FormLayout");
const Link_1 = require("../../../Link");
const constants_1 = require("./helpers/constants");
const LoginMain_styles_1 = require("./LoginMain.styles");
const maxLength_1 = require("../../helpers/maxLength");
const hooks_1 = require("../../hooks");
const types_1 = require("../../types");
const Notification_1 = require("../../../Notification");
const Spinner_1 = require("../../../Spinner");
const StackView_1 = require("../../../StackView");
const View_1 = require("../../../View");
exports.LoginMain = react_1.default.memo(function LoginMain({ initialValues, loading, forgotPasswordUrl, forgotPasswordTarget = Link_1.LINK_TARGET.blank, forgotPasswordOnClick, logIn, busyModel, onRequestMFACode, isExpired, notificationMessage, hasRememberMe, texts: customTexts, error: externalError, twoStepsLoginMode = false, stepLoginMode, onChangeStepLoginMode, maxLengthLogin, maxLengthPassword }) {
    const texts = Object.assign({}, constants_1.LOGIN_MAIN_DEFAULT_TEXTS, customTexts);
    const { loginRef, passwordRef } = (0, hooks_1.useAutoFocusLoginAndPassword)(twoStepsLoginMode && stepLoginMode === types_1.LOGIN_MODE_STEPS.password);
    const { getValue: getStepLoginMode } = (0, helpers_1.useStateValue)(stepLoginMode, [stepLoginMode]);
    const fieldRequired = (step) => {
        const requiredRule = (0, core_1.required)(texts.errorRequired);
        return (value, values, ruleTexts) => {
            if (!twoStepsLoginMode) {
                return requiredRule(value, values, ruleTexts);
            }
            return step === getStepLoginMode() ? requiredRule(value, values, ruleTexts) : '';
        };
    };
    const form = (0, core_1.useForm)({
        login: (initialValues === null || initialValues === void 0 ? void 0 : initialValues.login) || '',
        password: (initialValues === null || initialValues === void 0 ? void 0 : initialValues.password) || '',
        rememberMe: (initialValues === null || initialValues === void 0 ? void 0 : initialValues.rememberMe) || false
    }, {
        validationSchema: {
            login: [
                fieldRequired(types_1.LOGIN_MODE_STEPS.login),
                (0, maxLength_1.maxLength)(maxLengthLogin, texts.errorMaxLength)
            ],
            password: [
                fieldRequired(types_1.LOGIN_MODE_STEPS.password),
                (0, maxLength_1.maxLength)(maxLengthPassword, texts.errorMaxLength)
            ]
        }
    });
    const { value: { loading: internalLoading } } = (0, core_1.useModel)(busyModel);
    const { submit, isSubmitted, errorHighlight, fieldFocusHandler, keyDownHandler, hasError, error } = (0, hooks_1.useLoginFormSubmit)({
        form,
        handler: logIn,
        busyModel,
        error: externalError,
        onRequestMFACode: () => onRequestMFACode(form.getValue().login),
        twoStepsLoginMode,
        stepLoginMode,
        onChangeStepLoginMode
    });
    loading = loading || internalLoading;
    (0, react_1.useEffect)(() => {
        if (twoStepsLoginMode && stepLoginMode === types_1.LOGIN_MODE_STEPS.login) {
            form.model.update((values) => {
                values.password = '';
            });
        }
    }, [twoStepsLoginMode, stepLoginMode, form.model]);
    const renderFieldByStepMode = (fieldElement, needRender) => {
        if (!twoStepsLoginMode) {
            return fieldElement;
        }
        return needRender ? fieldElement : null;
    };
    const isCapsLockActive = (0, hooks_1.useCapsLockDetector)();
    const needShowExpiredWarning = isExpired && !isSubmitted;
    const hasWarning = isCapsLockActive || needShowExpiredWarning;
    const hasMessages = notificationMessage || hasError || hasWarning;
    const getShownForgotPasswordUrl = () => {
        if (!twoStepsLoginMode) {
            return !!forgotPasswordUrl;
        }
        return stepLoginMode === types_1.LOGIN_MODE_STEPS.password && !!forgotPasswordUrl;
    };
    const getShownRememberMe = () => {
        if (!twoStepsLoginMode) {
            return !!hasRememberMe;
        }
        return stepLoginMode === types_1.LOGIN_MODE_STEPS.login && !!hasRememberMe;
    };
    return (react_1.default.createElement(StackView_1.StackView, { direction: StackView_1.STACK_DIRECTION.column, gap: StackView_1.STACK_GAP.s, spaceTop: hasMessages ? View_1.INDENT.none : View_1.INDENT.m, onKeyDown: keyDownHandler },
        react_1.default.createElement(StackView_1.StackView, { align: StackView_1.STACK_ALIGN.start, direction: StackView_1.STACK_DIRECTION.column, gap: StackView_1.STACK_GAP.s },
            notificationMessage && !needShowExpiredWarning && (react_1.default.createElement(Notification_1.Notification, { status: typeof notificationMessage === 'string'
                    ? Notification_1.NOTIFICATION_STATUS.warning
                    : notificationMessage.type }, typeof notificationMessage === 'string'
                ? notificationMessage
                : notificationMessage.text)),
            hasError && react_1.default.createElement(ErrorMessage_1.ErrorMessage, null, error),
            needShowExpiredWarning && (react_1.default.createElement(Notification_1.Notification, { status: Notification_1.NOTIFICATION_STATUS.warning }, texts.warningSessionExpired)),
            isCapsLockActive && (react_1.default.createElement(Notification_1.Notification, { status: Notification_1.NOTIFICATION_STATUS.warning }, texts.warningCapsLockOn))),
        react_1.default.createElement(FormLayout_1.FormLayout, { disabled: loading },
            renderFieldByStepMode(react_1.default.createElement(fields_1.TextField, { field: form.fields.login, validateAfterTouch: false, controlRef: loginRef, placeholder: texts.placeholderLogin, size: public_1.CONTROL_SIZE.full, hasError: errorHighlight, onFocus: fieldFocusHandler }), stepLoginMode === types_1.LOGIN_MODE_STEPS.login),
            renderFieldByStepMode(react_1.default.createElement(fields_1.PasswordField, { field: form.fields.password, validateAfterTouch: false, controlRef: passwordRef, placeholder: texts.placeholderPassword, size: public_1.CONTROL_SIZE.full, hasError: errorHighlight, onFocus: fieldFocusHandler }), stepLoginMode === types_1.LOGIN_MODE_STEPS.password),
            getShownRememberMe() && (react_1.default.createElement(fields_1.CheckboxField, { field: form.fields.rememberMe, label: texts.rememberMe }))),
        react_1.default.createElement(StackView_1.StackView, { spaceTop: getShownRememberMe() ? View_1.INDENT.none : View_1.INDENT.s, direction: StackView_1.STACK_DIRECTION.row, gap: StackView_1.STACK_GAP.s, align: StackView_1.STACK_ALIGN.center },
            react_1.default.createElement(StackView_1.StackView, { direction: StackView_1.STACK_DIRECTION.row, gap: StackView_1.STACK_GAP.s, align: StackView_1.STACK_ALIGN.center },
                react_1.default.createElement(LoginMain_styles_1.StyledLoginMainButton, { onClick: submit, disabled: loading }, texts.actionLogIn),
                loading && react_1.default.createElement(Spinner_1.Spinner, null)),
            react_1.default.createElement(layouts_1.GrowView, null),
            getShownForgotPasswordUrl() && (react_1.default.createElement(Link_1.Link, { href: forgotPasswordUrl || '', onClick: forgotPasswordOnClick, target: forgotPasswordTarget, disabled: loading }, texts.actionForgotPassword)))));
});
//# sourceMappingURL=LoginMain.js.map